import { defineMessages } from '../define';

/**
 * The user administration screen. Role *names* live in status.ts because they
 * are shown all over the console; only the descriptions of what each role may
 * do belong here, next to the picker that explains the choice.
 */
export const users = defineMessages({
  en: {
    'users.title': 'Users',
    'users.description': 'Who can sign in to this console, and how much of it they can see.',
    'users.add': 'Add user',
    'users.create': 'Create user',

    'users.denied.title': 'Administrators only',
    'users.denied.description':
      'Managing accounts changes who can read every message in the system, so it is limited to admins and the owner. Ask one of them if you need access.',

    'users.column.user': 'User',
    'users.column.role': 'Role',
    'users.column.status': 'Status',
    'users.column.lastLogin': 'Last login',
    'users.column.created': 'Created',

    'users.actions.for': 'Actions for {name}',
    'users.resetPassword': 'Reset password',
    'users.activate': 'Activate',
    'users.deactivate': 'Deactivate',

    'users.delete.self': 'You cannot delete the account you are signed in with. Ask another admin.',
    'users.delete.hint': 'Cascades to their sessions, keys, webhooks and keywords.',
    'users.delete.title': 'Delete {name}?',
    'users.delete.description':
      'Their sessions, API keys, webhooks and keywords are deleted too. This cannot be undone.',
    'users.delete.confirm': 'Delete user',

    'users.empty.title': 'No users',
    'users.empty.description': 'Add the first one.',

    'users.toast.created': 'User created',
    'users.toast.updated': 'User updated',
    'users.toast.deleted': 'User deleted',
    'users.toast.activated': 'Account activated',
    'users.toast.deactivated': 'Account deactivated',
    'users.toast.passwordReset': 'Password reset',
    'users.toast.passwordReset.detail': 'That user has been signed out everywhere.',

    'users.dialog.edit': 'Edit user',
    'users.dialog.roleNote': 'Changing the role takes effect on their next request.',

    'users.field.name': 'Name',
    'users.field.email': 'Email',
    'users.field.role': 'Role',
    'users.field.password': 'Password',
    'users.field.newPassword': 'New password',

    'users.role.hint.VIEWER': 'read only',
    'users.role.hint.OPERATOR': 'run sessions and send',
    'users.role.hint.ADMIN': 'settings, users and audit',
    'users.role.hint.OWNER': 'everything, including other owners',
    'users.role.option': '{role} — {hint}',
    'users.role.ownerOnly': 'Only the owner can grant admin or owner.',

    'users.reset.description': '{name} will be signed out of every device.',
    'users.reset.thisUser': 'This user',

    'users.password.hint':
      'At least {min} characters. A phrase of three or four words beats a short scramble.',
    'users.password.more.one': '{count} more character needed.',
    'users.password.more.other': '{count} more characters needed.',
    'users.password.short': 'Short but acceptable',
    'users.password.reasonable': 'Reasonable',
    'users.password.strong': 'Strong',

    'users.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',
  },
  th: {
    'users.title': 'ผู้ใช้',
    'users.description': 'ใครเข้าใช้คอนโซลนี้ได้บ้าง และเห็นข้อมูลได้มากแค่ไหน',
    'users.add': 'เพิ่มผู้ใช้',
    'users.create': 'สร้างผู้ใช้',

    'users.denied.title': 'เฉพาะผู้ดูแลระบบเท่านั้น',
    'users.denied.description':
      'การจัดการบัญชีมีผลต่อสิทธิ์อ่านข้อความทั้งหมดในระบบ จึงจำกัดไว้เฉพาะผู้ดูแลระบบและเจ้าของ หากคุณจำเป็นต้องใช้ กรุณาติดต่อผู้ที่มีสิทธิ์',

    'users.column.user': 'ผู้ใช้',
    'users.column.role': 'บทบาท',
    'users.column.status': 'สถานะ',
    'users.column.lastLogin': 'เข้าใช้ล่าสุด',
    'users.column.created': 'สร้างเมื่อ',

    'users.actions.for': 'การทำงานสำหรับ {name}',
    'users.resetPassword': 'ตั้งรหัสผ่านใหม่',
    'users.activate': 'เปิดใช้งานบัญชี',
    'users.deactivate': 'ปิดใช้งานบัญชี',

    'users.delete.self': 'คุณลบบัญชีที่กำลังใช้งานอยู่ไม่ได้ กรุณาให้ผู้ดูแลระบบคนอื่นดำเนินการแทน',
    'users.delete.hint': 'จะลบเซสชัน คีย์ webhook และคำสำคัญของผู้ใช้รายนี้ไปด้วย',
    'users.delete.title': 'ลบ {name} หรือไม่',
    'users.delete.description':
      'เซสชัน API key webhook และคำสำคัญของผู้ใช้รายนี้จะถูกลบไปด้วย และไม่สามารถกู้คืนได้',
    'users.delete.confirm': 'ลบผู้ใช้',

    'users.empty.title': 'ยังไม่มีผู้ใช้',
    'users.empty.description': 'เพิ่มคนแรกได้เลย',

    'users.toast.created': 'สร้างผู้ใช้แล้ว',
    'users.toast.updated': 'อัปเดตผู้ใช้แล้ว',
    'users.toast.deleted': 'ลบผู้ใช้แล้ว',
    'users.toast.activated': 'เปิดใช้งานบัญชีแล้ว',
    'users.toast.deactivated': 'ปิดใช้งานบัญชีแล้ว',
    'users.toast.passwordReset': 'ตั้งรหัสผ่านใหม่แล้ว',
    'users.toast.passwordReset.detail': 'ผู้ใช้รายนี้ถูกออกจากระบบทุกอุปกรณ์แล้ว',

    'users.dialog.edit': 'แก้ไขผู้ใช้',
    'users.dialog.roleNote': 'การเปลี่ยนบทบาทจะมีผลตั้งแต่คำขอถัดไปของผู้ใช้',

    'users.field.name': 'ชื่อ',
    'users.field.email': 'อีเมล',
    'users.field.role': 'บทบาท',
    'users.field.password': 'รหัสผ่าน',
    'users.field.newPassword': 'รหัสผ่านใหม่',

    'users.role.hint.VIEWER': 'อ่านได้อย่างเดียว',
    'users.role.hint.OPERATOR': 'สั่งงานเซสชันและส่งข้อความได้',
    'users.role.hint.ADMIN': 'ตั้งค่าระบบ จัดการผู้ใช้ และดูบันทึกตรวจสอบ',
    'users.role.hint.OWNER': 'ทำได้ทุกอย่าง รวมถึงจัดการเจ้าของคนอื่น',
    'users.role.option': '{role} — {hint}',
    'users.role.ownerOnly': 'เฉพาะเจ้าของเท่านั้นที่กำหนดบทบาทผู้ดูแลระบบหรือเจ้าของได้',

    'users.reset.description': '{name} จะถูกออกจากระบบทุกอุปกรณ์',
    'users.reset.thisUser': 'ผู้ใช้รายนี้',

    'users.password.hint':
      'อย่างน้อย {min} ตัวอักษร วลีสามถึงสี่คำปลอดภัยกว่ารหัสสั้น ๆ ที่สลับตัวอักษร',
    'users.password.more.one': 'ต้องการอีก {count} ตัวอักษร',
    'users.password.more.other': 'ต้องการอีก {count} ตัวอักษร',
    'users.password.short': 'สั้นแต่พอใช้ได้',
    'users.password.reasonable': 'ใช้ได้',
    'users.password.strong': 'แข็งแรงดี',

    'users.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเชื่อมต่อเกตเวย์ได้',
  },
});
